// src/utils/flutterDetection.ts
// Flutter環境の検出と状態管理を行うユーティリティ

// Flutter関連のwindowオブジェクトの型定義
declare global {
  interface Window {
    FlutterCamera?: {
      postMessage: (message: string) => void;
    };
    FlutterNavigation?: {
      postMessage: (message: string) => void;
    };
    FlutterDebug?: {
      postMessage: (message: string) => void;
    };
    FlutterError?: {
      postMessage: (message: string) => void;
    };
    // Web でリフレッシュした最新トークンを Flutter 保存へ同期するためのチャネル
    FlutterTokenSync?: {
      postMessage: (message: string) => void;
    };
    // Web だけでトークンを回復できないとき、Flutter に無人再認証を要請するチャネル
    FlutterReauth?: {
      postMessage: (message: string) => void;
    };
    // カメラ/マイクの使用直前に、OS権限の確認・要求を Flutter に依頼するチャネル
    FlutterPermission?: {
      postMessage: (message: string) => void;
    };
    // FlutterPermission の結果を受け取るコールバック（kind, granted）
    __flutterPermissionResult?: (kind: string, granted: boolean) => void;
  }
}

const FLUTTER_STATE_KEY = 'isFlutterApp';

/**
 * Flutter環境かどうかを判定する
 * 永続化された状態と複数の条件をチェック
 */
export const isInFlutter = (): boolean => {
  // まずsessionStorageの永続化された状態をチェック
  if (typeof window !== 'undefined') {
    const persistedState = sessionStorage.getItem(FLUTTER_STATE_KEY);
    if (persistedState === 'true') {
      return true;
    }
  }
  
  // ブラウザ環境でない場合はfalse
  if (typeof window === 'undefined') {
    return false;
  }
  
  // 複数条件での判定（従来のロジック）
  const hasFlutterObjects = !!(
    window.FlutterCamera ||
    window.FlutterNavigation ||
    window.FlutterDebug ||
    window.FlutterError
  );
  
  // UserAgentでの判定
  const hasFlutterUserAgent = !!(
    window.navigator && 
    window.navigator.userAgent && 
    window.navigator.userAgent.includes('Flutter')
  );
  
  return hasFlutterObjects || hasFlutterUserAgent;
};

/**
 * Flutter状態を永続化する
 * アプリケーション初期化時や初回Flutter検出時に呼び出し
 */
export const setFlutterState = (): void => {
  if (typeof window !== 'undefined' && isInFlutter()) {
    sessionStorage.setItem(FLUTTER_STATE_KEY, 'true');
  }
};

/**
 * 現在のFlutter検出状況をログ出力（デバッグ用）
 */
export const debugFlutterDetection = (): void => {
  if (typeof window === 'undefined') {
    console.log('Flutter Detection: Not in browser environment');
    return;
  }
  
  const persistedState = sessionStorage.getItem(FLUTTER_STATE_KEY);
  const hasFlutterCamera = !!window.FlutterCamera;
  const hasFlutterNavigation = !!window.FlutterNavigation;
  const hasFlutterDebug = !!window.FlutterDebug;
  const hasFlutterError = !!window.FlutterError;
  const hasFlutterUserAgent = !!(
    window.navigator?.userAgent?.includes('Flutter')
  );
  
  console.log('Flutter Detection Debug:', {
    persistedState,
    hasFlutterCamera,
    hasFlutterNavigation,
    hasFlutterDebug,
    hasFlutterError,
    hasFlutterUserAgent,
    finalResult: isInFlutter()
  });
};